import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const emptyVendor = { name: '', service: '', contact: '' };

const Vendors = () => {
  const { t } = useTranslation();
  const [vendors, setVendors] = useState([]);
  const [vendor, setVendor] = useState(emptyVendor);
  const [editingIdx, setEditingIdx] = useState(null);
  const [editVendor, setEditVendor] = useState(emptyVendor);
  const card_id = localStorage.getItem('id');

  const fetchVendors = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/planner/vendors?card_id=${card_id}`);
      setVendors(response.data);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  }, [card_id]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const handleAdd = async e => {
    e.preventDefault();
    if (vendor.name.trim()) {
      try {
        const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/planner/vendors`, { ...vendor, card_id });
        setVendors([...vendors, response.data]);
        setVendor(emptyVendor);
      } catch (error) {
        console.error("Error adding vendor:", error);
      }
    }
  };

  const handleEdit = (idx) => {
    setEditingIdx(idx);
    setEditVendor(vendors[idx]);
  };

  const handleEditSave = async (id) => {
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/planner/vendors/${id}`, editVendor);
      const updatedVendors = vendors.map(v => (v.id === id ? response.data : v));
      setVendors(updatedVendors);
      setEditingIdx(null);
      setEditVendor(emptyVendor);
    } catch (error) {
      console.error("Error updating vendor:", error);
    }
  };

  const removeVendor = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/planner/vendors/${id}`);
      setVendors(vendors.filter(v => v.id !== id));
    } catch (error) {
      console.error("Error deleting vendor:", error);
    }
  };

  const handleClearVendors = async () => {
    if (window.confirm(t('vendors.clearConfirmation'))) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/planner/vendors/clear/${card_id}`);
        setVendors([]);
      } catch (error) {
        console.error("Error clearing vendors:", error);
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-10 px-2">
      <h1 className="text-3xl font-bold text-primary mb-4">{t('vendors.title')}</h1>
      <p className="text-foreground/70 mb-2">{t('vendors.description')}</p>

      {/* Form + Clear Button */}
      <div className="flex flex-wrap md:flex-nowrap gap-3 mb-6">
        <form className="flex flex-wrap gap-3 flex-1 min-w-0" onSubmit={handleAdd}>
          <input
            className="flex-1 border rounded px-3 py-2 text-base sm:text-lg min-w-[140px]"
            placeholder={t('vendors.vendorNamePlaceholder')}
            value={vendor.name}
            onChange={e => setVendor({ ...vendor, name: e.target.value })}
            required
          />
          <input
            className="flex-1 border rounded px-3 py-2 text-base sm:text-lg min-w-[140px]"
            placeholder={t('vendors.servicePlaceholder')}
            value={vendor.service}
            onChange={e => setVendor({ ...vendor, service: e.target.value })}
            required
          />
          <input
            className="flex-1 border rounded px-3 py-2 min-w-[140px]"
            placeholder={t('vendors.contactInfoPlaceholder')}
            value={vendor.contact}
            onChange={e => setVendor(v => ({ ...v, contact: e.target.value }))}
            required
          />
          <button
            type="submit"
            className="bg-primary text-white px-4 py-2 rounded hover:bg-secondary hover:text-gray-800 transition-colors w-full sm:w-auto"
          >
            {t('common.add')}
          </button>
        </form>

        <button
          onClick={handleClearVendors}
          className="bg-danger text-white px-4 py-2 rounded hover:bg-secondary hover:text-gray-800 transition-colors w-full md:w-auto"
          disabled={vendors.length === 0}
        >
          {t('vendors.clearAllVendors')}
        </button>
      </div>

      <ul className="space-y-4">
        {vendors.length === 0 && <li className="text-foreground/50">{t('vendors.noVendorsYet')}</li>}
        {vendors.map((v, idx) => (
          <li key={v.id} className="bg-white rounded shadow p-4 flex flex-col md:flex-row md:items-center gap-3">
            {editingIdx === idx ? (
              <>
                <input type="text" className="border rounded px-2 py-1 flex-1" value={editVendor.name} onChange={e => setEditVendor({ ...editVendor, name: e.target.value })} placeholder={t('vendors.vendorNamePlaceholder')} />
                <input type="text" className="border rounded px-2 py-1 flex-1" value={editVendor.service} onChange={e => setEditVendor({ ...editVendor, service: e.target.value })} placeholder={t('vendors.servicePlaceholder')} />
                <input type="text" className="border rounded px-2 py-1 flex-1" value={editVendor.contact} onChange={e => setEditVendor({ ...editVendor, contact: e.target.value })} placeholder={t('vendors.contactInfoPlaceholder')} />
                <button onClick={() => handleEditSave(v.id)} className="text-success font-bold px-2">{t('common.save')}</button>
                <button onClick={() => setEditingIdx(null)} className="text-danger px-2">{t('common.cancel')}</button>
              </>
            ) : (
              <>
                <span className="font-bold text-primary flex-1">{v.name}</span>
                <span className="text-info flex-1">{v.service}</span>
                <span className="flex-1">{v.contact}</span>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(idx)} className="text-info px-2">{t('common.edit')}</button>
                  <button onClick={() => removeVendor(v.id)} className="text-danger px-2">{t('common.delete')}</button>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Vendors;
